import { ref, onMounted, watch, computed } from 'vue';
import axios from 'axios';
import { DateTime } from 'luxon';
import VueSelect from 'vue-select';
import VueDatePicker from '@vuepic/vue-datepicker';
import { baseUrl } from '../../env';
import LoadingModal from '@/components/LoadingModal.vue';
import { useRoute } from 'vue-router'; // ✅ import useRoute

export default {
    name: 'ProjectSubmitterPage',
    components: { VueSelect, VueDatePicker, LoadingModal },

    setup() {
        const route = useRoute();
        const submitters = ref([]);
        const selectedSubmitter = ref([]);
        const submitterKeyword = ref('');
        const selectedTownship = ref(null)
        const date = ref({ from: null, to: null });
        const data = ref([]);
        const loading = ref(false);
        const loadingLabel = ref('Учитавање резултата...');
        const sortKey = ref('date');
        const sortOrders = ref({
            township: 1,
            submitter: 1,
            name: 1,
            date: -1,
            commissionMembers: 1,
            funds: 1
        });

        // Fetch submitters
        onMounted(async () => {
            const res = await axios.get(`${baseUrl}/projectSubmitters`);
            submitters.value = res.data.map(s => ({ id: s.id, name: s.name }));
            // ✅ Preselect submitter based on query
            const querySubmitterId = route.query.submitter;
            if (querySubmitterId) {
                const found = submitters.value.find(m => String(m.id) === String(querySubmitterId));
                if (found) {
                    selectedSubmitter.value = [found];
                    fetchData(); 
                }
            }
            initSelectedTownship();
        });

        // Watch keyword and auto-select all matches
        watch(submitterKeyword, (keyword) => {
            const lower = keyword.toLowerCase().trim();
            if (!lower) {
                selectedSubmitter.value = [];
                return;
            }
            const matches = submitters.value.filter(s =>
                s.name.toLowerCase().includes(lower)
            );
            selectedSubmitter.value = matches;
            initSelectedTownship();
        });

        // Filtered data based on selected submitters and dates
        const filteredData = computed(() => {
            if (!data.value) return [];
            return data.value.filter(project => {
                if (selectedSubmitter.value.length > 0) {
                    const ids = selectedSubmitter.value.map(s => s.id);
                    if (!ids.includes(project.submitter?.id)) return false;
                }

                const projectDate = DateTime.fromISO(project.date);
                if (date.value.from && projectDate < DateTime.fromJSDate(date.value.from)) return false;
                if (date.value.to && projectDate > DateTime.fromJSDate(date.value.to)) return false;

                return true;
            });
        });

       const filteredDataSorted = computed(() => {
    if (!filteredData.value) return [];
    const order = (key) => sortOrders.value[key] ?? 1;

    return [...filteredData.value].sort((a, b) => {
        let aVal, bVal;

        switch (sortKey.value) {
            case 'township':
                aVal = a.township || '';
                bVal = b.township || '';
                break;
            case 'submitter':
                aVal = a.submitter?.name || '';
                bVal = b.submitter?.name || '';
                break;
            case 'name':
                aVal = a.name || '';
                bVal = b.name || '';
                break;
            case 'date':
                // a.date and b.date are guaranteed Luxon DateTime now
                if (a.date < b.date) return -1 * order('date');
                if (a.date > b.date) return 1 * order('date');
                return 0;
            case 'commissionMembers':
                aVal = a.commissionMembers?.length || 0;
                bVal = b.commissionMembers?.length || 0;
                break;
            case 'funds':
                aVal = a.funds || 0;
                bVal = b.funds || 0;
                break;
            default:
                aVal = '';
                bVal = '';
        }

        if (aVal < bVal) return -1 * order(sortKey.value);
        if (aVal > bVal) return 1 * order(sortKey.value);
        return 0;
    });
});


        const fetchData = async () => {
            loading.value = true;
            try {
                const from = date.value.from
                    ? DateTime.fromJSDate(date.value.from).toFormat('dd.MM.yyyy.')
                    : '01.01.1971.';
                const to = date.value.to
                    ? DateTime.fromJSDate(date.value.to).toFormat('dd.MM.yyyy.')
                    : DateTime.local().toFormat('dd.MM.yyyy.');

                const submitterIds = selectedSubmitter.value.length > 0
                    ? selectedSubmitter.value.map(s => s.id).join(',')
                    : 'all';

                 // ✅ preuzmi 'jedinica' iz query stringa ako postoji
                const jedinicaId = route.query.jedinica || (selectedTownship.value ? selectedTownship.value.id : '');
        
                const url = `${baseUrl}/projects/submitter/${submitterIds}/${from}/${to}/${jedinicaId || ''}`;
                const response = await axios.get(url);
                data.value = response.data.map(project => {
                let date;

                if (project.date) {
                    // Try parsing as ISO
                    let parsed = DateTime.fromISO(project.date);
                    if (parsed.isValid) {
                        date = parsed;
                    } else {
                        // fallback for dd.MM.yyyy.
                        const parts = project.date.split('.');
                        if (parts.length >= 3) {
                            date = DateTime.fromObject({
                                day: parseInt(parts[0]),
                                month: parseInt(parts[1]),
                                year: parseInt(parts[2])
                            });
                        }
                    }
                }

                // ✅ fallback to epoch if invalid/empty
                if (!date || !date.isValid) {
                    date = DateTime.fromMillis(0);
                }

                return {
                    ...project,
                    date, // always Luxon DateTime
                    commissionMembers: project.commissionMembers || []
                };
            });


            } catch (err) {
                console.error('Error fetching projects:', err);
            } finally {
                loading.value = false;
            }
        };

        function sortBy(key) {
            if (sortKey.value === key) {
                sortOrders.value[key] = sortOrders.value[key] * -1;
            } else {
                sortKey.value = key;
            }
        }

        const filteredCommissionMembers = (commissionMembers) =>
            commissionMembers?.filter(member => member.chosen === 1) || [];


        function initSelectedTownship() {
            const jedinicaId = route.query.jedinica
            const jedinicaName = route.query.naziv || null

            if (jedinicaId) {
                selectedTownship.value = {
                id: jedinicaId,
                name: jedinicaName || `Јединица ${jedinicaId}`
                }
            } else {
                selectedTownship.value = null
            }
        }

        return {
            submitters,
            selectedSubmitter,
            submitterKeyword,
            date,
            data,
            loading,
            loadingLabel,
            sortKey,
            sortOrders,
            filteredData,
            filteredDataSorted,
            sortBy,
            fetchData,
            filteredCommissionMembers,
            // ✅ Use filtered data instead of raw data
            fundsSum: computed(() => {
                // choose which filtered dataset you display:
                const activeData = filteredDataSorted.value?.length ? filteredDataSorted.value : filteredData.value;

                return activeData.reduce(
                (sum, project) => sum + (project.funds || 0),
                0
                ).toLocaleString('sr');
            })
        };
    }
};
